import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Zustand store for current owner state management
 * Provides persistent storage across page reloads and better state management
 */
export const useOwnerStore = create(
  persist(
    (set, get) => ({
      // Current owner state
      currentOwner: null,
      userOwners: [],
      loading: false,
      error: null,
      lastAccessedOwnerId: null, // For remembering last accessed owner

      // Actions
      setCurrentOwner: (owner) => {
        set({ 
          currentOwner: owner,
          lastAccessedOwnerId: owner?.id || null,
          error: null 
        });
      },

      setUserOwners: (owners) => {
        set({ userOwners: owners });
      },

      setLoading: (loading) => {
        set({ loading });
      },

      setError: (error) => {
        set({ error });
      },

      clearOwner: () => {
        set({ 
          currentOwner: null,
          lastAccessedOwnerId: null,
          error: null 
        });
      },

      // Helper to get current owner ID
      getCurrentOwnerId: () => {
        return get().currentOwner?.id || null;
      },

      // Helper to get current owner slug
      getCurrentOwnerSlug: () => {
        return get().currentOwner?.slug || null;
      },

      // Switch to a specific owner
      switchOwner: (owner) => {
        set({ 
          currentOwner: owner,
          lastAccessedOwnerId: owner?.id || null,
          error: null 
        });
      },

      // Find owner by slug from userOwners
      findOwnerBySlug: (slug) => {
        const { userOwners } = get();
        return userOwners.find(o => o.slug === slug) || null;
      },

      // Find owner by ID from userOwners
      findOwnerById: (id) => {
        const { userOwners } = get();
        return userOwners.find(o => o.id === id) || null;
      },

      // Get last accessed owner
      getLastAccessedOwner: () => {
        const { lastAccessedOwnerId, userOwners } = get();
        if (!lastAccessedOwnerId) return null;
        
        // Filter out placeholder UUIDs
        const placeholderUUID = '11111111-1111-1111-1111-111111111111';
        if (lastAccessedOwnerId === placeholderUUID) {
          console.warn('Found placeholder UUID in lastAccessedOwnerId, clearing it');
          set({ lastAccessedOwnerId: null });
          return null;
        }
        
        return userOwners.find(o => o.id === lastAccessedOwnerId) || null;
      },
    }),
    {
      name: 'rentaloop-owner-storage', // localStorage key
      partialize: (state) => ({
        // Only persist these fields
        lastAccessedOwnerId: state.lastAccessedOwnerId,
        // Don't persist currentOwner, userOwners as they should be fetched fresh
        // but we can persist the ID for quick recovery
      }),
    }
  )
);

